import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router";
import Cookies from "js-cookie";

import { GetSelf } from "../../services/users";
import { AddUserToGroup } from "../../services/groups";
import { UserNonexistentError, ExpiredTokenError } from "../../models/errors/networkError";
import { Context } from "../../models/context";
import { User } from "../../models/user";
import "./login.css";
import { useAlerts } from "../../contexts/AlertContext";

const OAUTH_URL = `https://oauthfs.rll.byu.edu/login.html`;
const SKIP_OAUTH = false;
export default function Login() {
    const context = useContext(Context);
    const params = new URLSearchParams(window.location.search);
    const navigate = useNavigate();

    const [showReload, setShowReload] = useState<boolean>(false);
    const [showRedirectMessage, setShowRedirectMessage] = useState<boolean>(false);
    const [redirectPage, setRedirectPage] = useState<string>('');

    const { addAlert, addError } = useAlerts();

    useEffect(() => {
        if (params.get("error")) {
            console.error("Authentication error:", params.get("error"));
            addAlert("Authentication failed. Please try again.", 5000, "error", true);
            setShowReload(true);
        } else if (Cookies.get("login") || Cookies.get("auth_code")) {
            // have a code or a cookie
            handleLoggedIn();
        } else {
            const oAuthUrl = getOAuthUrl();
            if (oAuthUrl) {
                window.location.href = oAuthUrl;
            } else if (SKIP_OAUTH && Cookies.get("auth_code")) {
                handleLoggedIn();
            }
        }
    }, []);

    const handleLoggedIn = async () => {
        // get query params
        let nextPage: string = params.get("redirect") || "/";
        const groupId: string | null = params.get("group");

        const cookieDomain = window.location.hostname.split("gs-interface.").slice(-1)[0]
        // reset cookie
        let newLogin = Cookies.get("login");
        let authCode = Cookies.get("auth_code") || "";
        if (newLogin) {
            const login = JSON.parse(newLogin);
            authCode = login["auth-token"];
            Cookies.remove("login", { domain: cookieDomain });
        }
        Cookies.set("auth_code", authCode, { expires: 1, domain: cookieDomain, sameSite: "Lax", secure: true });
        window.history.replaceState({}, document.title, window.location.pathname);

        // get user info
        const user = await handleGetUser(nextPage, groupId);
        if (!user) return;

        try {
            if (groupId) {
                console.log("Adding user to group:", groupId);
                if (user.id) {
                    const service = new AddUserToGroup(groupId, user.id);
                    await service.request({}, user ?? { authToken: Cookies.get("auth_code")! });
                } else {
                    console.warn("Cannot add user to group because user id is missing.");
                }
            }
        } catch (err) {
            addError(err as Error, "Error adding user to group");
            console.error("Error adding user to group:", err);
        }

        let entryPoint = params.get('entryPoint');

        if (entryPoint) {
            if (!entryPoint.startsWith('/')) {
                entryPoint = '/' + entryPoint
            }
            console.log("Returning to: ", entryPoint);
            navigate(entryPoint);
            return;
        }

        try {
            if (nextPage != "/") {
                console.log("Redirecting to:", nextPage);
                if (!nextPage.startsWith("http")) {
                    nextPage = "https://" + nextPage;
                }
                const redirectParams = new URLSearchParams();
                window.location.assign(nextPage + `?${redirectParams.toString()}`);
            }
            else navigate(nextPage ?? "/");
            setShowRedirectMessage(true);
            setRedirectPage(nextPage);
        } catch (err) {
            addError(err as Error, "Error redirecting");
            console.error("Error redirecting:", err);
        }
    }

    const handleGetUser = async (nextPage: string, groupId: string | null): Promise<User | null> => {
        try {
            const user = await (new GetSelf().request({ authToken: Cookies.get("auth_code") || "" }));
            user.authToken = Cookies.get("auth_code");
            context.setUser(user);
            console.log("User info:", user);
            return user;
        } catch (err) {
            if (err instanceof UserNonexistentError) {
                // user doesn't exist, create them
                console.error(`User not found. Redirecting to create user page with params ${nextPage}, ${groupId}.`);
                const search = new URLSearchParams();
                if (nextPage) search.set("redirect", nextPage);
                if (groupId) search.set("group", groupId);

                navigate({
                    pathname: "/create-user",
                    search: `?${search.toString()}`
                });
                return null;
            } else if (err instanceof ExpiredTokenError) {
                // Token expired, refresh cookie
                console.log("refreshing cookie")
                const cookieDomain = window.location.hostname.split("gs-interface.").slice(-1)[0]
                Cookies.remove("login", { domain: cookieDomain });
                Cookies.remove("auth_code", { domain: cookieDomain });
                const oAuthUrl = getOAuthUrl();
                if (oAuthUrl) window.location.href = oAuthUrl;
                return null;
            } else {
                // other error
                addAlert("Authentication failed. Please try again.", 5000, "error", true);
                setShowReload(true);
                console.error("Authentication error:", err);
                return null;
            }
        }
    };

    // oauth stuff
    const getOAuthUrl = () => {
        console.log("getting oauth url");
        const currentUrl = window.location.href;
        let oauth_url = OAUTH_URL
        if (currentUrl.includes("localhost")) {
            if (SKIP_OAUTH) {
                const testCode = "test-code";
                Cookies.set("auth_code", testCode, { expires: 1, domain: "localhost", sameSite: "Lax", secure: true });
                return null;
            }
            oauth_url = "http://localhost:5000/login.html";
        }
        if (currentUrl.includes("rll-dev")) {
            oauth_url = OAUTH_URL.replace("rll", "rll-dev");
        }

        const redirectUri = new URL(currentUrl);
        redirectUri.pathname = "login";
        return `${oauth_url}?redirect_uri=${encodeURIComponent(redirectUri.href)}`;
    }

    return <>
        {showReload &&
            <button id="reload-button" onClick={() => {
                window.location.search = "";
                setShowReload(false);
                window.location.reload();
            }}>Reload</button>
        }
        {showRedirectMessage &&
            <div className="loader">
                <div className="loading-ring"><div></div><div></div><div></div><div></div></div>
                <div className="loading-text">
                    <div className="loading-message">Redirecting you to {redirectPage}</div>
                </div>
                <div className="loading-warning">Please do not refresh or navigate away.</div>
            </div>
        }
    </>;
}
